import ResponseModel from "../models/response.model";
import axiosSetup from "../utils/axiosSetup";
import { IOrderAddress } from "../models/Order.model";
import { EwbDtls } from "../models/invoice/invoice.model";
const basePath = "/einvoice";

export const generateIRNApi = async ({
  documentNumber,
  orderId,
  EwbDtls,
  warehouseId,
  billingAddress,
  shippingAddress,
  goodsIds,
}: {
  documentNumber: string;
  orderId: string;
  EwbDtls?: EwbDtls;
  warehouseId?: string;
  billingAddress?: IOrderAddress;
  shippingAddress?: IOrderAddress;
  goodsIds: string[];
}) => {
  const res = await axiosSetup.post(
    `${basePath}/generate-irn`,
    {
      documentNumber,
      EwbDtls,
      billingAddress,
      shippingAddress,
      goodsIds,
    },
    { params: { orderId, warehouseId } },
  );
  return res.data as ResponseModel;
};

export const getEInvoiceByIdApi = async ({
  invoiceId,
}: {
  invoiceId: string;
}): Promise<ResponseModel> => {
  const res = await axiosSetup.get(`${basePath}/${invoiceId}/invoice`);
  return res.data as ResponseModel;
};

export const getAllEInvoicesApi = async (
  query?: Record<string, any>,
): Promise<ResponseModel> => {
  const res = await axiosSetup.get(`${basePath}/all`, { params: query });
  return res.data as ResponseModel;
};

export const markAsTallyTransferredApi = async ({
  invoiceId,
}: {
  invoiceId: string;
}): Promise<ResponseModel> => {
  const res = await axiosSetup.patch(
    `${basePath}/${invoiceId}/mark-as-tally-transferred`,
  );
  return res.data as ResponseModel;
};

export const getDistanceApi = async ({
  originPin,
  destinationPin,
}: {
  originPin: string;
  destinationPin: string;
}): Promise<ResponseModel> => {
  const res = await axiosSetup.get(`${basePath}/pincode-distance`, {
    params: {
      originPin,
      destinationPin,
    },
  });
  return res.data as ResponseModel;
};

export const getGoodsForInvoiceApi = async ({
  orderId,
}: {
  orderId: string;
}): Promise<ResponseModel> => {
  const res = await axiosSetup.get(`${basePath}/orders/${orderId}/goods`);
  return res.data as ResponseModel;
};

export const cancelEInvoiceApi = async ({
  invoiceId,
  orderId,
}: {
  invoiceId: string;
  orderId: string;
}): Promise<ResponseModel> => {
  const res = await axiosSetup.patch(
    `${basePath}/cancel/${orderId}/${invoiceId}`,
  );
  return res.data as ResponseModel;
};

export const getNextEInvoiceNumberApi = async (): Promise<ResponseModel> => {
  const res = await axiosSetup.get(`${basePath}/next-invoice-number`);
  return res.data as ResponseModel;
};

//EPC Invoice API
export interface GenerateEpcInvoiceRequest {
  kw: number; // plant capacity, > 0
  paymentId: string; // whatsappPayment _id (Mongo ObjectId)
  invoiceNumber: string;
  lineItems: {
    description: string;
    percentage: number;
    amount: number;
  }[];

  buyer: {
    name: string; // required
    state: string; // required
    // B2C customer address
    line1: string; // required
    line2?: string;
    city: string; // required
    pincode: string | number; // required, 6 digits
  };

  ewb?: {
    transId?: string; // transporter GSTIN – transId OR transName required
    transName?: string;
    vehicleNo?: string; // vehicleNo OR transDocNo required
    transDocNo?: string; // LR/RR/airway bill no.
    transDocDt?: string; // "DD/MM/YYYY"; defaults to today
    transMode?: "Road" | "Rail" | "Air" | "Ship" | "Sea" | 1 | 2 | 3 | 4; // default Road (1)
    vehicleType?: "R" | "O"; // R = regular, O = over-dimensional; default "R"
    distance?: number; // km; 0 lets the portal auto-calculate
  };
}

export const generateEPCInvoiceApi = async (
  data: GenerateEpcInvoiceRequest,
): Promise<ResponseModel> => {
  const res = await axiosSetup.post(`${basePath}/generate-epc-invoice`, {
    ...data,
  });
  return res.data as ResponseModel;
};

export const cancelEPCInvoiceApi = async ({
  invoiceId,
  paymentId,
}: {
  invoiceId: string;
  paymentId: string;
}): Promise<ResponseModel> => {
  const res = await axiosSetup.patch(
    `${basePath}/cancel-epc/${paymentId}/${invoiceId}`,
  );
  return res.data as ResponseModel;
};

export const getDispatchDetailsApi = async ({
  orderId,
  quotationId,
  isEpc,
}: {
  orderId?: string;
  quotationId?: string;
  isEpc?: boolean;
}): Promise<ResponseModel> => {
  const dispatchId = orderId || quotationId;
  if (!dispatchId) {
    throw new Error("An order ID or quotation ID is required.");
  }

  const res = await axiosSetup.get(
    `${basePath}/orders/${dispatchId}/dispatch-details`,
    {
      params: {
        isEpc,
        ...(!orderId && quotationId ? { quotationId } : {}),
      },
    },
  );
  return res.data as ResponseModel;
};

export const getEinvoicesByCustomerIdApi = async ({
  customerId,
}: {
  customerId: string;
}): Promise<ResponseModel> => {
  const res = await axiosSetup.get(
    `${basePath}/customer/${customerId}/invoices`,
  );
  return res.data as ResponseModel;
};
